import { useAudienceStore } from '@/stores/audience-store';
import type { AudienceDraft, DraftPatch } from '../types/draft';

/** Snapshot the subset of store state that makes up a draft patch. */
export function draftFromStore(state: ReturnType<typeof useAudienceStore.getState>): DraftPatch {
  return {
    target: {
      name: state.target.name,
      brief: state.target.brief,
      filters: state.target.filters,
      files: state.target.files,
      leadGoal: state.target.leadGoal,
      leadsPerCompany: state.target.leadsPerCompany,
      campaignTimeframe: state.target.campaignTimeframe,
      startDate: state.target.startDate,
      endDate: state.target.endDate,
      impressionGoal: state.target.impressionGoal,
      additionalDetails: state.target.additionalDetails,
      dealReference: state.target.dealReference,
      intentTopicSelections: state.target.intentTopicSelections,
      // Without this the autosave PATCH never carries technographics, so a picked technology is
      // held in memory, shown in the UI, and silently lost on reload.
      technographicSelections: state.target.technographicSelections,
      signalSources: state.target.signalSources,
      play: state.target.play,
      companyDomains: state.target.companyDomains,
    },
    content: {
      links: state.content.links,
      files: state.content.files,
      requestedProducts: state.content.requestedProducts,
    },
    products: {
      selectedProducts: state.products.selectedProducts,
      budget: state.products.budget,
      productAnswers: state.products.productAnswers,
    },
  };
}

/**
 * True when a local draft patch holds any content worth prompting the user
 * to recover. Empty drafts (everything blank / null) are silently discarded.
 */
export function hasRecoverableContent(p: DraftPatch): boolean {
  const t = p.target;
  if (t) {
    if (t.brief && t.brief.trim().length > 0) return true;
    if (t.files && t.files.length > 0) return true;
    if (t.filters && Object.values(t.filters).some((v) => Array.isArray(v) && v.length > 0)) {
      return true;
    }
    if (t.leadGoal !== null && t.leadGoal !== undefined) return true;
    if (t.impressionGoal !== null && t.impressionGoal !== undefined) return true;
    // NPJB-552 — a dates-only draft is real user input worth recovering. One
    // branch, not two: this function is already far over Sonar's cognitive-
    // complexity limit, so don't add avoidable paths to it.
    if (t.startDate || t.endDate) return true;
    if (t.intentTopicSelections && t.intentTopicSelections.length > 0) return true;
    // A technographics-only or domains-only (NPJB-763) draft is real user input — without this
    // it is treated as empty and never persisted at all. One branch for both: this function is
    // already far over Sonar's cognitive-complexity limit.
    const pickedAccounts =
      (t.technographicSelections?.length ?? 0) + (t.companyDomains?.domains.length ?? 0);
    if (pickedAccounts > 0) return true;
  }
  const c = p.content;
  if (c) {
    if (c.links && c.links.length > 0) return true;
    if (c.files && c.files.length > 0) return true;
    if (c.requestedProducts && c.requestedProducts.length > 0) return true;
  }
  const pr = p.products;
  if (pr) {
    if (pr.selectedProducts && pr.selectedProducts.length > 0) return true;
    if (pr.budget !== null && pr.budget !== undefined) return true;
    if (pr.productAnswers && Object.keys(pr.productAnswers).length > 0) return true;
  }
  return false;
}

/**
 * NPJB-763 — the play and the domain list, set together. Kept out of `hydrateStoreFrom`,
 * which is already far over Sonar's cognitive-complexity limit.
 */
function hydratePlayTargeting(t: NonNullable<DraftPatch['target']>): void {
  if (t.play === undefined && t.companyDomains === undefined) return;
  const current = useAudienceStore.getState().target;
  useAudienceStore
    .getState()
    .carryPlay(t.play ?? current.play, t.companyDomains ?? current.companyDomains);
}

/**
 * Apply a server draft or patch to the store by routing through the
 * matching setters. Callers are expected to guard this with a hydration
 * flag so the store subscriber doesn't echo these writes out as edits.
 */
export function hydrateStoreFrom(source: AudienceDraft | DraftPatch): void {
  const s = useAudienceStore.getState();
  const t = 'target' in source ? source.target : undefined;
  if (t) {
    if (t.name !== undefined) s.setName(t.name);
    if (t.brief !== undefined) s.setBrief(t.brief);
    if (t.filters !== undefined) s.setFilters(t.filters);
    if (t.files !== undefined) s.setFiles(t.files);
    if (t.leadGoal !== undefined) s.setLeadGoal(t.leadGoal);
    if (t.leadsPerCompany !== undefined) {
      s.setLeadsPerCompany(t.leadsPerCompany);
    }
    if (t.campaignTimeframe !== undefined) {
      s.setCampaignTimeframe(t.campaignTimeframe);
    }
    if (t.startDate !== undefined) s.setStartDate(t.startDate);
    if (t.endDate !== undefined) s.setEndDate(t.endDate);
    if (t.impressionGoal !== undefined) s.setImpressionGoal(t.impressionGoal);
    if (t.additionalDetails !== undefined) {
      s.setAdditionalDetails(t.additionalDetails ?? '');
    }
    if (t.dealReference !== undefined) {
      s.setDealReference(t.dealReference);
    }
    if (t.intentTopicSelections !== undefined) {
      s.setIntentTopics(t.intentTopicSelections);
    }
    if (t.technographicSelections !== undefined) {
      s.setTechnographics(t.technographicSelections);
    }
    if (t.signalSources !== undefined) {
      s.setSignalSources(t.signalSources);
    }
    hydratePlayTargeting(t);
  }
  const c = 'content' in source ? source.content : undefined;
  if (c) {
    if (c.links !== undefined) s.setContentLinks(c.links);
    if (c.files !== undefined) s.setContentFiles(c.files);
    if (c.requestedProducts !== undefined) s.setRequestedContentProducts(c.requestedProducts);
  }
  const p = 'products' in source ? source.products : undefined;
  if (p) {
    if (p.selectedProducts !== undefined) s.setSelectedProducts(p.selectedProducts);
    if (p.budget !== undefined) s.setBudget(p.budget);
    if (p.productAnswers !== undefined) s.setProductAnswers(p.productAnswers);
  }
}
